import React, { useState } from 'react';
import PhoneFilters from '../components/PhoneFilters';
import { PhoneField, toPlus } from '../components/PhoneField';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from "../hooks/useDebounce.ts";

import {
  useMembers,
  useMemberStats,
  useCreateMember,
  useUpdateMember,
  useDeleteMember,
  useExportMembers,
} from '../hooks/useMembers.ts';
import { useSubgroups } from '../hooks/useSubgroups.ts';
import {
  usePendingMembers,
  usePendingMemberStats,
  useGenerateOnboardingLink,
  useOnboardingLinks,
  useApprovePendingMember,
  useRejectPendingMember,
  useBulkApprovePendingMembers,
  useBulkRejectPendingMembers,
  useDeletePendingMember,
} from '../hooks/usePendingMembers.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { toast } from 'react-hot-toast';
import Table from '../components/Table.tsx';
import { Search, Download, Edit, Trash, Plus, Eye, EyeOff, Settings, Shield, Link, Check, X, Clock, Users, Copy, FileText, Home } from 'lucide-react';
import Modal from '../components/Modal.tsx';
import { useQueryClient } from '@tanstack/react-query';
import getApiErrorMessage from '../utils/helper.js';
import api from '../utils/axios.ts';
import Select from '../components/ui/Select.tsx';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import SubgroupMembersModal from '../components/SubgroupMembersModal';

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const PrivacyBanner = () => {
  const { data: profile } = useGetProfile();
  const isAdmin = useIsAdmin();
  
  if (!profile?.data?.current_group) return null;

  const memberVisibilityEnabled = profile.data.current_group.can_view_other_members ?? false;
  const canViewOthers = isAdmin || memberVisibilityEnabled;

  if (canViewOthers) return null;

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <div className="flex items-start space-x-3">
        <div className="p-2 rounded-full bg-yellow-100 text-yellow-600">
          <EyeOff size={20} />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-gray-900">
            🔒 Limited Visibility Mode
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            You can only view your own member information. Contact your group admin to enable full member visibility.
          </p>
        </div>
      </div>
    </div>
  );
};

function getInitials(name) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function AvatarName({ name, email }) {
  return (
    <div className="flex items-center">
      <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium mr-3">
        {getInitials(name)}
      </div>
      <div>
        <div className="text-sm font-medium text-gray-900">{name}</div>
        {email && <div className="text-xs text-gray-500">{email}</div>}
      </div>
    </div>
  );
}

function ContactInfo({ phone, email }) {
  const displayPhone = phone && phone.startsWith('VIRTUAL_') ? '-' : phone;
  return (
    <div>
      <div className="text-sm text-gray-900">{displayPhone}</div>
      {email && <div className="text-xs text-gray-500">{email}</div>}
    </div>
  );
}

function StatusBadge({ is_active }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        is_active
          ? 'bg-green-100 text-green-800'
          : 'bg-red-100 text-red-800'
      }`}
    >
      {is_active ? 'Active' : 'Inactive'}
    </span>
  );
}

function PendingStatusBadge({ status }) {
  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
    approved: { color: 'bg-green-100 text-green-800', text: 'Approved' },
    rejected: { color: 'bg-red-100 text-red-800', text: 'Rejected' }
  };

  const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.text}
    </span>
  );
}

function formatDateDMY(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB');
}

const MembersPage = () => {
  const { can } = usePermissions();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('members'); // 'members' or 'pending'
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPendingStatus, setSelectedPendingStatus] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [adminReassignCandidates, setAdminReassignCandidates] = useState(null);
  const [reassignAdminTo, setReassignAdminTo] = useState('');
  const [page, setPage] = useState(1);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Pending members state
  const [showGenerateLinkModal, setShowGenerateLinkModal] = useState(false);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [selectedPendingMembers, setSelectedPendingMembers] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [showAdditionalDataModal, setShowAdditionalDataModal] = useState(false);
  const [selectedAdditionalData, setSelectedAdditionalData] = useState(null);

  // Member tier top-up state (Core plan, Kes 250 -> Kes 500 to unlock unlimited members)
  const [showMemberTopUpModal, setShowMemberTopUpModal] = useState(false);
  const [topUpPhoneNumber, setTopUpPhoneNumber] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [topUpMessage, setTopUpMessage] = useState('');

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data: membersData, isLoading: isLoadingMembers } = useMembers({
    search: debouncedSearchQuery,
    status: selectedStatus,
    sort_field: sortField,
    sort_direction: sortDirection,
    page,
    per_page: 15,
  });

  const { data: statsData } = useMemberStats();
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();
  const createMember = useCreateMember();
  const updateMember = useUpdateMember();
  const deleteMember = useDeleteMember();
  const exportMembers = useExportMembers();
  const queryClient = useQueryClient();

  // Pending members hooks
  const { data: pendingMembersData, isLoading: isLoadingPendingMembers } = usePendingMembers({
    search: debouncedSearchQuery,
    status: selectedPendingStatus !== 'all' ? selectedPendingStatus : undefined,
    page,
    per_page: 15,
  });

  const { data: pendingStatsData } = usePendingMemberStats();
  const { data: existingLinks } = useOnboardingLinks();
  const generateOnboardingLink = useGenerateOnboardingLink();
  const approvePendingMember = useApprovePendingMember();
  const rejectPendingMember = useRejectPendingMember();
  const bulkApprovePendingMembers = useBulkApprovePendingMembers();
  const bulkRejectPendingMembers = useBulkRejectPendingMembers();
  const deletePendingMember = useDeletePendingMember();

  // Extract member visibility setting from profile
  const memberVisibilityEnabled = profile?.data?.current_group?.can_view_other_members ?? false;
  const canViewOthers = isAdmin || memberVisibilityEnabled;
  const isRestrictedMode = !isAdmin && !memberVisibilityEnabled;

  // House Management & Irregular feature
  const isHouseManagement = Number(profile?.data?.current_group?.is_house_management) === 1;
  const isIrregular = Number(profile?.data?.current_group?.is_irregular) === 1;
  const houseLabel = profile?.data?.current_group?.house_label || 'House';
  const tenantLabel = profile?.data?.current_group?.tenant_label || 'Tenant';
  const { data: subgroupsData } = useSubgroups();
  const [managingHouseMember, setManagingHouseMember] = useState(null);

  const handleExport = () => {
    exportMembers.mutate(
      {},
      {
        onSuccess: () => {
          toast.success('Members exported successfully');
        },
        onError: (error) => {
          toast.error(getApiErrorMessage(error) || 'Failed to export members');
        }
      }
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddMember = () => {
    const scopedVillageId = profile?.data?.scoped_village_id || profile?.data?.member?.scoped_village_id;
    setSelectedMember({
      name: '',
      member_no: '',
      id_no: '',
      email: '',
      date_registered: new Date().toISOString().split('T')[0],
      status: 'active',
      phone_number: '',
      village_id: scopedVillageId || null,
    });
    setShowAddMemberModal(true);
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setShowAddMemberModal(true);
  };

  const handleDeleteMember = (member) => {
    setSelectedMember(member);
    setAdminReassignCandidates(null);
    setReassignAdminTo('');
    setShowConfirmDeleteModal(true);
  };

  const handleViewMemberProfile = (memberId) => {
    navigate(`/members/${memberId}`);
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    const formData = {
      name: selectedMember?.name || '',
      member_no: selectedMember?.member_no || '',
      id_no: selectedMember?.id_no || '',
      email: selectedMember?.email || '',
      date_registered: selectedMember?.date_registered || new Date().toISOString().split('T')[0],
      status: (selectedMember?.status ?? 'active').toLowerCase(),
      phone_number: selectedMember?.phone_number || '',
      subgroup_id: selectedMember?.subgroup_id || null,
      village_id: selectedMember?.village_id || null,
      scoped_village_id: selectedMember?.scoped_village_id || null,
    };

    if (selectedMember?.id) {
      updateMember.mutate(
        { id: selectedMember.id, ...formData },
        {
          onSuccess: () => {
            toast.success('Member updated successfully');
            handleModalClose();
          },
          onError: (error) => {
            const apiMessage = getApiErrorMessage(error);
            toast.error(apiMessage);
          },
        }
      );
    } else {
      // Include optional opening contribution fields for new members
      const createData = { ...formData };
      if (selectedMember?.initial_contribution_amount && Number(selectedMember.initial_contribution_amount) > 0) {
        createData.initial_contribution_amount = selectedMember.initial_contribution_amount;
        if (selectedMember?.initial_contribution_date) {
          createData.initial_contribution_date = selectedMember.initial_contribution_date;
        }
        if (selectedMember?.initial_contribution_notes) {
          createData.initial_contribution_notes = selectedMember.initial_contribution_notes;
        }
      }
      createMember.mutate(createData, {
        onSuccess: () => {
          toast.success('Member added successfully');
          handleModalClose();
        },
        onError: (error) => {
          if (error?.response?.data?.code === 'MEMBER_TIER_LIMIT') {
            handleModalClose();
            setShowMemberTopUpModal(true);
            return;
          }
          const apiMessage = getApiErrorMessage(error);
          toast.error(apiMessage);
        },
      });
    }
  };

  const handleConfirmDelete = () => {
    if (selectedMember) {
      const params = reassignAdminTo
        ? { id: selectedMember.id, reassign_admin_to: Number(reassignAdminTo) }
        : selectedMember.id;
      deleteMember.mutate(params, {
        onSuccess: () => {
          toast.success('Member deleted successfully');
          setShowConfirmDeleteModal(false);
          setAdminReassignCandidates(null);
          setReassignAdminTo('');
        },
        onError: (error) => {
          if (error?.response?.data?.code === 'ADMIN_REASSIGNMENT_REQUIRED') {
            setAdminReassignCandidates(error.response.data.candidates || []);
            return;
          }
          const apiMessage = getApiErrorMessage(error);
          toast.error(apiMessage);
        },
      });
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      setUploadError('Please select a file.');
      return;
    }
    setUploading(true);
    setUploadError('');
    const formData = new FormData();
    formData.append('file', bulkFile);
    try {
      await api.post('/members/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Members uploaded successfully!');
      setShowBulkUploadModal(false);
      setBulkFile(null);
      queryClient.invalidateQueries({ queryKey: ['members'] });
    } catch (err) {
      if (err?.response?.data?.code === 'MEMBER_TIER_LIMIT') {
        setShowBulkUploadModal(false);
        setShowMemberTopUpModal(true);
        setUploading(false);
        return;
      }

      const backendMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.errors ||
        err.message ||
        'Upload failed. Please check your file and try again.';

      setUploadError(backendMsg);
      toast.error(
        typeof backendMsg === 'string'
          ? backendMsg
          : JSON.stringify(backendMsg)
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/members/template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'members_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleModalClose = () => {
    setShowAddMemberModal(false);
    setSelectedMember(null);
  };

  // Pending members handlers
  const handleGenerateLink = async () => {
    generateOnboardingLink.mutate(
      { expires_in_days: expiresInDays },
      {
        onSuccess: (data) => {
          setGeneratedLink(data);
          toast.success('Onboarding link generated successfully');
        },
        onError: (error) => {
          toast.error('Failed to generate onboarding link');
        }
      }
    );
  };

  const handleCopyLink = () => {
    if (generatedLink?.url) {
      navigator.clipboard.writeText(generatedLink.url);
      toast.success('Link copied to clipboard');
    }
  };

  const handleApprovePendingMember = (id) => {
    approvePendingMember.mutate(id, {
      onSuccess: () => {
        toast.success('Pending member approved successfully');
        setSelectedPendingMembers(new Set());
      },
      onError: (error) => {
        toast.error('Failed to approve pending member');
      }
    });
  };

  const handleRejectPendingMember = (id, reason) => {
    rejectPendingMember.mutate(
      { id, reason },
      {
        onSuccess: () => {
          toast.success('Pending member rejected successfully');
          setRejectionReason('');
        },
        onError: (error) => {
          toast.error('Failed to reject pending member');
        }
      }
    );
  };

  const handleBulkAction = (action) => {
    if (selectedPendingMembers.size === 0) {
      toast.error('Please select pending members first');
      return;
    }

    setBulkAction(action);
    setShowBulkActionModal(true);
  };

  const handleBulkActionConfirm = () => {
    const ids = Array.from(selectedPendingMembers);

    if (bulkAction === 'approve') {
      bulkApprovePendingMembers.mutate(ids, {
        onSuccess: (data) => {
          toast.success(`Bulk approval completed: ${data.data.approved_count} approved, ${data.data.failed_count} failed`);
          setShowBulkActionModal(false);
          setSelectedPendingMembers(new Set());
        },
        onError: (error) => {
          toast.error('Failed to process bulk approval');
        }
      });
    } else if (bulkAction === 'reject') {
      bulkRejectPendingMembers.mutate(
        { ids, reason: rejectionReason },
        {
          onSuccess: (data) => {
            toast.success(`Bulk rejection completed: ${data.data.rejected_count} rejected, ${data.data.failed_count} failed`);
            setShowBulkActionModal(false);
            setSelectedPendingMembers(new Set());
            setRejectionReason('');
          },
          onError: (error) => {
            toast.error('Failed to process bulk rejection');
          }
        }
      );
    }
  };

  const handlePendingMemberSelect = (id) => {
    const newSelected = new Set(selectedPendingMembers);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedPendingMembers(newSelected);
  };

  const handleSelectAllPendingMembers = () => {
    if (pendingMembersData?.data) {
      if (selectedPendingMembers.size === pendingMembersData.data.length) {
        setSelectedPendingMembers(new Set());
      } else {
        setSelectedPendingMembers(new Set(pendingMembersData.data.map(member => member.id)));
      }
    }
  };

  const columns = [
    {
      label: isHouseManagement ? tenantLabel : 'Member',
      field: 'name',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => <AvatarName name={row.name} email={row.email} />,
    },
    ...((isHouseManagement || isIrregular) ? [{
      label: isHouseManagement ? houseLabel : 'Subgroup',
      field: 'subgroup_name',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm text-gray-700',
      render: (row) => row.subgroup_name || <span className="text-gray-400">Unassigned</span>,
    }] : []),
    ...(isIrregular ? [{
      label: 'Village / Region',
      field: 'village_name',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm text-gray-700',
      render: (row) => row.village_name || <span className="text-gray-400">Unassigned</span>,
    }] : []),
    {
      label: 'ID Number',
      field: 'id_no',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm text-gray-700',
      render: (row) => row.id_no || <span className="text-gray-400">-</span>,
    },
    {
      label: 'Contact',
      field: 'phone_number',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => <ContactInfo phone={row.phone_number} email={row.email} />,
    },
    {
      label: 'Status',
      field: 'is_active',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        <div className="flex items-center gap-2">
          <StatusBadge is_active={row.is_active} />
          {isHouseManagement && row.is_subgroup && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              <Home size={11} /> {houseLabel}
            </span>
          )}
        </div>
      ),
    },
    {
      label: 'Registered',
      field: 'date_registered',
      className: 'hidden sm:table-cell text-gray-500 px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      sortable: true,
      render: (row) => formatDateDMY(row.date_registered),
    },
    {
      label: 'Additional Data',
      field: 'additional_data',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        row.additional_data ? (
          <button
            onClick={() => {
              setSelectedAdditionalData(row.additional_data);
              setShowAdditionalDataModal(true);
            }}
            className="flex items-center text-blue-600 hover:text-blue-800"
            title="View Additional Data"
          >
            <FileText size={16} className="mr-1" />
            View
          </button>
        ) : (
          <span className="text-gray-400 text-xs">None</span>
        )
      ),
    },
    {
      label: 'Actions',
      field: 'actions',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewMemberProfile(row.id)}
            className="text-green-600 hover:text-green-800"
            title="View Profile"
          >
            <Eye size={16} />
          </button>
          {/* House management button */}
          {isHouseManagement && row.is_subgroup && isAdmin && (
            <button
              onClick={() => setManagingHouseMember(row)}
              className="text-indigo-600 hover:text-indigo-800"
              title={`Manage ${tenantLabel}s`}
            >
              <Home size={16} />
            </button>
          )}
          {can(PERMISSIONS.EDIT_MEMBER) && (
          <button
            onClick={() => handleEditMember(row)}
            className="text-blue-600 hover:text-blue-800"
            title="Edit"
          >
            <Edit size={16} />
          </button>
          )}
          {can(PERMISSIONS.DELETE_MEMBER) && (
          <button
            onClick={() => handleDeleteMember(row)}
            className="text-red-600 hover:text-red-800"
            title="Delete"
          >
            <Trash size={16} />
          </button>
          )}
        </div>
      ),
    },
  ];

  const pendingColumns = [
    {
      label: isHouseManagement ? houseLabel : 'Member',
      field: 'name',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={selectedPendingMembers.has(row.id)}
            onChange={() => handlePendingMemberSelect(row.id)}
            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <AvatarName name={row.name} email={row.email} />
        </div>
      ),
    },
    {
      label: 'Contact',
      field: 'phone_number',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => <ContactInfo phone={row.phone_number} email={row.email} />,
    },
    {
      label: 'Status',
      field: 'status',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => <PendingStatusBadge status={row.status} />,
    },
    {
      label: 'Applied',
      field: 'created_at',
      className: 'hidden sm:table-cell text-gray-500 px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => formatDateDMY(row.created_at),
    },
    {
      label: 'Additional Data',
      field: 'additional_data',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        row.additional_data ? (
          <button
            onClick={() => {
              setSelectedAdditionalData(row.additional_data);
              setShowAdditionalDataModal(true);
            }}
            className="flex items-center text-blue-600 hover:text-blue-800"
            title="View Additional Data"
          >
            <FileText size={16} className="mr-1" />
            View
          </button>
        ) : (
          <span className="text-gray-400 text-xs">None</span>
        )
      ),
    },
    {
      label: 'Actions',
      field: 'actions',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        <div className="flex space-x-2">
          {row.status === 'pending' && (
            <>
              <button
                onClick={() => handleApprovePendingMember(row.id)}
                className="text-green-600 hover:text-green-800"
                title="Approve"
              >
                <Check size={16} />
              </button>
              {can(PERMISSIONS.REJECT_PENDING_MEMBERS) && (
              <button
                onClick={() => handleRejectPendingMember(row.id, '')}
                className="text-red-600 hover:text-red-800"
                title="Reject"
              >
                <X size={16} />
              </button>
              )}
            </>
          )}
          {can(PERMISSIONS.DELETE_PENDING_MEMBERS) && (
          <button
            onClick={() => deletePendingMember.mutate(row.id)}
            className="text-red-600 hover:text-red-800"
            title="Delete"
          >
            <Trash size={16} />
          </button>
          )}
        </div>
      ),
    },
  ];

  const pendingStatusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const statsCards = [
    {
      label: isHouseManagement ? `Total ${tenantLabel}s` : 'Total Members',
      value: statsData?.data?.total || 0,
      subLabel: isHouseManagement ? `All registered ${tenantLabel.toLowerCase()}s` : 'All registered members',
      valueColor: 'text-blue-600',
    },
    {
      label: isHouseManagement ? `Active ${tenantLabel}s` : 'Active Members',
      value: statsData?.data?.active || 0,
      subLabel: 'Currently active',
      valueColor: 'text-green-600',
    },
    {
      label: isHouseManagement ? `Inactive ${tenantLabel}s` : 'Inactive Members',
      value: statsData?.data?.inactive || 0,
      subLabel: 'Currently inactive',
      valueColor: 'text-red-600',
    },
  ];

  const pendingStatsCards = [
    {
      label: 'Pending Approval',
      value: pendingStatsData?.total_pending || 0,
      subLabel: 'Awaiting approval',
      valueColor: 'text-yellow-600',
    },
    {
      label: 'Approved',
      value: pendingStatsData?.total_approved || 0,
      subLabel: 'Successfully approved',
      valueColor: 'text-green-600',
    },
    {
      label: 'Rejected',
      value: pendingStatsData?.total_rejected || 0,
      subLabel: 'Rejected applications',
      valueColor: 'text-red-600',
    },
  ];

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_MEMBERS}>
    <div className="min-h-screen">
      <div className="container mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <h2 className="text-2xl font-bold">
            {isHouseManagement ? `${tenantLabel}s` : 'Members'}
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {activeTab === 'members' && (
              <>
                <PermissionGate permission={PERMISSIONS.EXPORT_MEMBERS}>
                {(isAdmin || canViewOthers) && (
                  <button
                    onClick={handleExport}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto"
                  >
                    <Download size={16} className="mr-2" />
                    Export
                  </button>
                )}
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.IMPORT_MEMBERS}>
                {isAdmin && (
                  <button
                    onClick={() => setShowBulkUploadModal(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto"
                  >
                    Bulk Upload
                  </button>
                )}
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.CREATE_MEMBER}>
                {isAdmin && (
                    <>
                      <button
                          onClick={handleAddMember}
                          className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow text-sm"
                      >
                        <Plus size={16} className="mr-2"/>
                        Add {isHouseManagement ? tenantLabel : 'Member'}
                      </button>
                    </>
                )}
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.GENERATE_ONBOARDING_LINKS}>
                {isAdmin && (
                    <>
                      { !profile.data.current_group.registration_form ? (
                        <button
                            onClick={() => setShowGenerateLinkModal(true)}
                            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 w-full sm:w-auto"
                        >
                          <Link size={16} className="mr-2"/>
                          Generate Link
                        </button>
                      ) : (
                        <button
                            onClick={() => {
                              const registrationUrl = `https://twachanga.co.ke/registration/${profile.data.current_group.registration_form}`;
                              navigator.clipboard.writeText(registrationUrl).then(() => {
                                // You can add a toast notification here if you have one
                                alert('Registration link copied to clipboard!');
                              });
                            }}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto"
                        >
                          <Copy size={16} className="mr-2"/>
                          Copy Link
                        </button>
                      )}
                    </>
                )}
                </PermissionGate>

              </>
            )}
            {activeTab === 'pending' && isAdmin && (
                <>
                  {selectedPendingMembers.size > 0 && (
                      <>
                        <button
                            onClick={() => handleBulkAction('approve')}
                            className="flex items-center px-4 py-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 text-sm"
                    >
                      <Check size={16} className="mr-2" />
                      Approve Selected
                    </button>
                    <button
                      onClick={() => handleBulkAction('reject')}
                      className="flex items-center px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm"
                    >
                      <X size={16} className="mr-2" />
                      Reject Selected
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('members')}
            className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
              activeTab === 'members'
                ? 'bg-white dark:!bg-slate-800 text-blue-900 dark:text-blue-300 shadow-sm border border-blue-200 dark:border-slate-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800/50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Users size={18} className={activeTab === 'members' ? 'text-blue-600' : 'text-gray-500'} />
              <span>{isHouseManagement ? `${tenantLabel}s` : 'Members'}</span>
              <span className={`px-2 py-1 text-xs rounded-full ${
                activeTab === 'members' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300'
              }`}>
                {statsData?.data?.total || 0}
              </span>
            </div>
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'pending'
                  ? 'bg-white dark:!bg-slate-800 text-orange-600 dark:text-orange-400 shadow-sm border border-orange-200 dark:border-slate-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800/50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Clock size={18} className={activeTab === 'pending' ? 'text-orange-600' : 'text-gray-500'} />
                <span>Pending Approval</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  activeTab === 'pending' ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300' : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300'
                }`}>
                  {pendingStatsData?.total_pending || 0}
                </span>
              </div>
            </button>
          )}
        </div>

        <PrivacyBanner />

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 kpi-grid">
          {(activeTab === 'members' ? statsCards : pendingStatsCards).map((card, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow p-6">
              <p className="text-sm text-gray-500 mb-1">{card.label}</p>
              <h3 className={`text-xl font-bold ${card.valueColor}`}>{card.value}</h3>
              <div className="mt-2 text-sm text-gray-500">{card.subLabel}</div>
            </div>
          ))}
        </div>

        {(canViewOthers || isAdmin) && (
          <div className="bg-white rounded-xl shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <input 
                    type="text"
                    placeholder={activeTab === 'members' ? "Search by name, phone, email or ID..." : "Search pending members..."}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
                </div>
              </div>
              <PhoneFilters count={(activeTab === 'members' ? selectedStatus : selectedPendingStatus) && (activeTab === 'members' ? selectedStatus : selectedPendingStatus) !== 'all' ? 1 : 0}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <div className="relative">
                  <Select
                    value={(activeTab === 'members' ? statusOptions : pendingStatusOptions).find(option => option.value === (activeTab === 'members' ? selectedStatus : selectedPendingStatus))}
                    onChange={(selectedOption) => activeTab === 'members' ? setSelectedStatus(selectedOption.value) : setSelectedPendingStatus(selectedOption.value)}
                    options={activeTab === 'members' ? statusOptions : pendingStatusOptions}
                    className="text-base"
                    classNamePrefix="select"
                    isSearchable={true}
                    placeholder="Select status..."
                  />
                </div>
              </div>
              </PhoneFilters>
            </div>
          </div>
        )}

        <Table
          columns={activeTab === 'members' ? columns : pendingColumns}
          data={activeTab === 'members' ? (membersData?.data || []) : (pendingMembersData?.data || [])}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          page={page}
          perPage={15}
          total={activeTab === 'members' ? (membersData?.meta?.total || 0) : (pendingMembersData?.meta?.total || 0)}
          onPageChange={setPage}
          loading={activeTab === 'members' ? isLoadingMembers : isLoadingPendingMembers}
        />

        {/* Add Member Modal */}
        <Modal
          isOpen={showAddMemberModal}
          onClose={handleModalClose}
          title={selectedMember?.id ? `Edit ${isHouseManagement ? houseLabel : 'Member'}` : `Add New ${isHouseManagement ? houseLabel : 'Member'}`}
        >
          <form onSubmit={handleModalSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedMember?.name || ''}
                onChange={(e) => setSelectedMember({ ...selectedMember, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isHouseManagement ? `${tenantLabel} Number` : 'Member Number'}
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedMember?.member_no || ''}
                onChange={(e) => setSelectedMember({ ...selectedMember, member_no: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ID Number</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedMember?.id_no || ''}
                onChange={(e) => setSelectedMember({ ...selectedMember, id_no: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedMember?.email || ''}
                onChange={(e) => setSelectedMember({ ...selectedMember, email: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <PhoneField
                bare
                label="Phone Number"
                value={toPlus(selectedMember?.phone_number)}
                onChange={(v) => setSelectedMember({ ...selectedMember, phone_number: v })}
                hint="Members outside Kenya: start with + and the country code, for example +255..."
              />
            </div>
            {(isHouseManagement || isIrregular || (subgroupsData?.data && subgroupsData.data.length > 0)) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned {isHouseManagement ? houseLabel : 'Subgroup'}
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedMember?.subgroup_id || ''}
                  onChange={(e) => setSelectedMember({ ...selectedMember, subgroup_id: e.target.value || null })}
                >
                  <option value="">None / Unassigned</option>
                  {(subgroupsData?.data || []).filter(sub => !sub.type || sub.type === 'subgroup').map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {isIrregular && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned Village / Region
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={selectedMember?.village_id || (profile?.data?.scoped_village_id || profile?.data?.member?.scoped_village_id) || ''}
                  disabled={!!(profile?.data?.scoped_village_id || profile?.data?.member?.scoped_village_id)}
                  onChange={(e) => setSelectedMember({ ...selectedMember, village_id: e.target.value || null })}
                >
                  <option value="">None / Unassigned</option>
                  {(subgroupsData?.data || []).filter(sub => sub.type === 'village').map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
                {!!(profile?.data?.scoped_village_id || profile?.data?.member?.scoped_village_id) && (
                  <p className="text-xs text-blue-600 mt-1 font-medium">Automatically assigned to your village</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Registered *</label>
              <input
                type="date"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedMember?.date_registered || ''}
                onChange={(e) => setSelectedMember({ ...selectedMember, date_registered: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedMember?.status || 'active'}
                onChange={(e) => setSelectedMember({ ...selectedMember, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {!selectedMember?.id && (
              <div className="pt-3 border-t border-gray-200 space-y-3">
                <h4 className="text-sm font-semibold text-gray-800">Prior Contribution (Opening Balance)</h4>
                <p className="text-xs text-gray-500">Optional: Record an opening balance for money this member contributed prior to joining Twachanga.</p>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Opening Contribution Amount (KES)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 15000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={selectedMember?.initial_contribution_amount || ''}
                    onChange={(e) => setSelectedMember({ ...selectedMember, initial_contribution_amount: e.target.value })}
                  />
                </div>
                {Number(selectedMember?.initial_contribution_amount || 0) > 0 && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Contribution Date</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        value={selectedMember?.initial_contribution_date || selectedMember?.date_registered || ''}
                        onChange={(e) => setSelectedMember({ ...selectedMember, initial_contribution_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Description / Notes</label>
                      <input
                        type="text"
                        placeholder="e.g. Contribution prior to onboarding"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        value={selectedMember?.initial_contribution_notes || ''}
                        onChange={(e) => setSelectedMember({ ...selectedMember, initial_contribution_notes: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleModalClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {selectedMember?.id ? 'Update' : 'Add'} {isHouseManagement ? tenantLabel : 'Member'}
              </button>
            </div>
          </form>
        </Modal>

        {/* Generate Onboarding Link Modal */}
        <Modal
          isOpen={showGenerateLinkModal}
          onClose={() => {
            setShowGenerateLinkModal(false);
            setGeneratedLink(null);
          }}
          title="Generate Onboarding Link"
        >
          <div className="space-y-4">
            {/* Existing Links Section */}
            {existingLinks && existingLinks.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Existing Active Links</h3>
                <p className="text-sm text-gray-600 mb-3">
                  These are your current active onboarding links. You can copy and share any of these links with potential members. 
                  Each link can be used multiple times until it expires.
                </p>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {existingLinks.map((link) => (
                    <div key={link.id} className="bg-gray-50 border border-gray-200 rounded-md p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">Link {link.id}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            link.days_remaining > 7 ? 'bg-green-100 text-green-800' : 
                            link.days_remaining > 3 ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-red-100 text-red-800'
                          }`}>
                            {link.days_remaining} days left
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(link.url);
                            toast.success('Link copied to clipboard');
                          }}
                          className="text-blue-600 hover:text-blue-800"
                          title="Copy link"
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Usage:</span> {link.usage_count} times
                      </div>
                      {link.last_used_at && (
                        <div className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Last used:</span> {new Date(link.last_used_at).toLocaleDateString()}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 break-all">
                        {link.url}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Generate New Link Section */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Generate New Link</h3>
              <p className="text-sm text-gray-600 mb-3">
                Create a new onboarding link with a custom expiration period. New links are useful when you need 
                different expiration times or want to track usage separately.
              </p>
              {!generatedLink ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link Expires In (Days)</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Choose how long this link will remain active (1-30 days). Longer periods are useful for ongoing recruitment campaigns.
                    </p>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={expiresInDays}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value >= 1 && value <= 30) {
                          setExpiresInDays(value);
                        } else if (e.target.value === '') {
                          setExpiresInDays('');
                        }
                      }}
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowGenerateLinkModal(false)}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGenerateLink}
                      disabled={generateOnboardingLink.isPending || !expiresInDays || expiresInDays < 1 || expiresInDays > 30}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                    >
                      {generateOnboardingLink.isPending ? 'Generating...' : 'Generate Link'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex items-center">
                      <Check size={20} className="text-green-600 mr-2" />
                      <span className="text-green-800 font-medium">Link Generated Successfully!</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Onboarding Link</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Copy this link and share it with potential members. They can use it to submit their membership applications.
                    </p>
                    <div className="flex">
                      <input
                        type="text"
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50"
                        value={generatedLink.url}
                      />
                      <button
                        onClick={handleCopyLink}
                        className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>This link will expire on: {new Date(generatedLink.expires_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowGenerateLinkModal(false);
                        setGeneratedLink(null);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Done
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal>

        {/* Bulk Action Modal */}
        <Modal
          isOpen={showBulkActionModal}
          onClose={() => setShowBulkActionModal(false)}
          title={`Bulk ${bulkAction === 'approve' ? 'Approve' : 'Reject'} Pending Members`}
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              {selectedPendingMembers.size} pending member(s) will be {bulkAction === 'approve' ? 'approved' : 'rejected'}.
            </p>
            {bulkAction === 'reject' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason (Optional)</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a reason for rejection..."
                />
              </div>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowBulkActionModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkActionConfirm}
                disabled={bulkAction === 'approve' ? bulkApprovePendingMembers.isPending : bulkRejectPendingMembers.isPending}
                className={`px-4 py-2 text-white rounded-md disabled:bg-gray-400 ${
                  bulkAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {bulkAction === 'approve' ? 'Approve' : 'Reject'} Selected
              </button>
            </div>
          </div>
        </Modal>

        {/* Bulk Upload Modal */}
        {showBulkUploadModal && (
          <Modal
            isOpen={showBulkUploadModal}
            onClose={() => setShowBulkUploadModal(false)}
            title={isHouseManagement ? `Bulk Upload ${tenantLabel}s` : 'Bulk Upload Members'}
            footer={
              <>
                <button
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  onClick={() => setShowBulkUploadModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                  onClick={handleBulkUpload}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </>
            }
          >
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Upload a CSV or Excel file with the following columns:
                </p>
                <ul className="text-sm text-gray-600 list-disc list-inside mb-4">
                  <li>Name (required)</li>
                  <li>Phone Number (required)</li>
                  {/* <li>Email (required)</li> */}
                  {/* <li>ID Number (required)</li> */}
                </ul>
                <button
                  onClick={handleDownloadTemplate}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 mb-4"
                >
                  <Download size={16} />
                  Download Template
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Excel File</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setBulkFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {uploadError && (
                <div className="bg-red-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md p-3">
                  <p className="text-red-800 text-sm">{uploadError}</p>
                </div>
              )}
            </div>
          </Modal>
        )}

        {/* Confirm Delete Modal */}
        {showConfirmDeleteModal && (
          <Modal
            isOpen={showConfirmDeleteModal}
            onClose={() => {
              setShowConfirmDeleteModal(false);
              setAdminReassignCandidates(null);
              setReassignAdminTo('');
            }}
            title="Confirm Delete"
            footer={
              <>
                <button
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  onClick={() => {
                    setShowConfirmDeleteModal(false);
                    setAdminReassignCandidates(null);
                    setReassignAdminTo('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleConfirmDelete}
                  disabled={adminReassignCandidates !== null && !reassignAdminTo}
                >
                  Delete
                </button>
              </>
            }
          >
            <div className="space-y-4">
              {adminReassignCandidates === null ? (
                <p className="text-gray-600">
                  Are you sure you want to delete <strong>{selectedMember?.name}</strong>? This action cannot be undone.
                </p>
              ) : (
                <>
                  <p className="text-gray-600">
                    <strong>{selectedMember?.name}</strong> is the group admin, so another member must take over as admin first.
                  </p>
                  {adminReassignCandidates.length === 0 ? (
                    <p className="text-red-600 text-sm">
                      There are no other active members to become admin, so this member cannot be deleted.
                    </p>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New admin</label>
                      <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={reassignAdminTo}
                        onChange={(e) => setReassignAdminTo(e.target.value)}
                      >
                        <option value="">Select a member...</option>
                        {adminReassignCandidates.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.phone_number})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          </Modal>
        )}

        {/* Additional Data Modal */}
        <Modal
          isOpen={showAdditionalDataModal}
          onClose={() => setShowAdditionalDataModal(false)}
          title="Additional Member Data"
          footer={
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
              onClick={() => setShowAdditionalDataModal(false)}
            >
              Close
            </button>
          }
        >
          <div className="space-y-4">
            {selectedAdditionalData && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Registration Form Data</h3>
                <div className="space-y-3">
                  {(() => {
                    // Parse the data if it's a string, otherwise use as is
                    let parsedData = selectedAdditionalData;
                    if (typeof selectedAdditionalData === 'string') {
                      try {
                        parsedData = JSON.parse(selectedAdditionalData);
                      } catch (error) {
                        console.error('Failed to parse additional_data:', error);
                        parsedData = { raw_data: selectedAdditionalData };
                      }
                    }
                    
                    // Handle both object and array formats
                    if (Array.isArray(parsedData)) {
                      return parsedData.map((item, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="text-sm font-medium text-gray-700 mb-2">Item {index + 1}</div>
                          <pre className="text-xs text-gray-600 overflow-x-auto">
                            {JSON.stringify(item, null, 2)}
                          </pre>
                        </div>
                      ));
                    } else if (typeof parsedData === 'object' && parsedData !== null) {
                      return Object.entries(parsedData).map(([key, value]) => (
                        <div key={key} className="border-b border-gray-200 pb-2 last:border-b-0">
                          <div className="text-sm font-medium text-gray-700 capitalize">
                            {key.replace(/_/g, ' ')}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {value === null || value === undefined ? (
                              <span className="text-gray-400">N/A</span>
                            ) : typeof value === 'object' ? (
                              <pre className="text-xs bg-white p-2 rounded border overflow-x-auto">
                                {JSON.stringify(value, null, 2)}
                              </pre>
                            ) : (key === 'photograph_path' || key === 'photograph' || key.includes('photo')) && value && (value.toString().includes('registration-photos/') || value.toString().includes('storage/')) ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-blue-600 font-medium">Photo:</span>
                                  <a 
                                    href={`https://twachanga.co.ke/storage/${value}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                                  >
                                    <FileText size={14} />
                                    View Photo
                                  </a>
                                  <span className="text-gray-500 text-xs">({value})</span>
                                </div>
                                <div className="mt-2">
                                  <img 
                                    src={`https://twachanga.co.ke/storage/${value}`}
                                    alt="Registration Photo"
                                    className="w-20 h-20 object-cover rounded border border-gray-200"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <span className="break-words">{value.toString()}</span>
                            )}
                          </div>
                        </div>
                      ));
                    } else {
                      return (
                        <div className="text-sm text-gray-600">
                          <pre className="text-xs bg-white p-2 rounded border overflow-x-auto">
                            {JSON.stringify(selectedAdditionalData, null, 2)}
                          </pre>
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>
            )}
          </div>
        </Modal>

        {/* Member Tier Top-Up Modal - Core plan, Kes 250 -> Kes 500 to unlock unlimited members */}
        {showMemberTopUpModal && (
          <Modal
            isOpen={showMemberTopUpModal}
            onClose={() => {
              setShowMemberTopUpModal(false);
              setTopUpPhoneNumber('');
              setTopUpMessage('');
              setTopUpLoading(false);
            }}
            title="Unlock Unlimited Members"
            size="md"
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Your group has reached 9 members on the Kes 250 Core plan. Pay Kes 500/month to unlock
                unlimited members - this is a one-time payment that permanently sets your rate to Kes 500/month.
              </p>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={topUpPhoneNumber}
                  onChange={(e) => setTopUpPhoneNumber(e.target.value)}
                  placeholder="254700000000"
                  className="block px-3 py-2 w-full bg-gray-50 rounded-md border border-gray-100 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                  disabled={topUpLoading}
                />
                <small className="mt-1 text-xs text-gray-500">Enter your M-Pesa registered phone number</small>
              </div>

              {topUpMessage && (
                <div className="text-sm text-blue-700 bg-blue-50 rounded-md p-3">{topUpMessage}</div>
              )}

              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                  onClick={() => {
                    setShowMemberTopUpModal(false);
                    setTopUpPhoneNumber('');
                    setTopUpMessage('');
                  }}
                  disabled={topUpLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  disabled={topUpLoading || !topUpPhoneNumber}
                  onClick={async () => {
                    setTopUpLoading(true);
                    setTopUpMessage('');
                    try {
                      const response = await api.post('/plans/topup-member-tier', {
                        phone_number: topUpPhoneNumber,
                      });
                      setTopUpMessage(
                        response.data?.message || 'Top-up initiated. Check your M-PESA to complete payment.'
                      );
                      toast.success('Check your phone to complete the Kes 500 payment.');
                    } catch (error) {
                      const apiMessage = getApiErrorMessage(error);
                      setTopUpMessage(apiMessage);
                      toast.error(apiMessage);
                    } finally {
                      setTopUpLoading(false);
                    }
                  }}
                >
                  {topUpLoading ? 'Sending…' : 'Pay Kes 500'}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
    {/* Subgroup Members Modal - manages tenants under a house */}
    {managingHouseMember && (
      <SubgroupMembersModal
        member={managingHouseMember}
        tenantLabel={tenantLabel}
        onClose={() => setManagingHouseMember(null)}
      />
    )}
    </PermissionPage>
  );
};

export default MembersPage;